import Dropdown from '../../atoms/Dropdown.js';
import VanillaCalendar from 'vanilla-calendar-pro';
import { nextTick } from '@studiometa/js-toolkit/utils';
import 'vanilla-calendar-pro/build/vanilla-calendar.min.css';

/**
 * @typedef {{}} DatePickerProps
 */

/**
 * DatePicker DatePicker.
 * @augments {Base<DatePickerProps>}
 */
export default class DatePicker extends Dropdown {
  /**
   * Class config.
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    name: 'DatePicker',
    refs: [
      'btn',
      'dropdown',
      'container',
      'fromInput',
      'toInput',
      'placeholderStart',
      'placeholderEnd',
      'close[]',
    ],
    emits: ['submit'],
    options: {
      selectedDates: {
        value: '',
        type: String,
      },
      startOff: {
        value: '',
        type: String,
      },
      endOff: {
        value: '',
        type: String,
      },
      single: {
        value: false,
        type: Boolean,
      },
      goToDate: {
        value: '',
        type: String,
      },
    },
  };

  /**
   *
   */
  mounted() {
    this.breakpoints = ['xxs', 'xs'];
    this.initDatepicker();
  }

  /**
   *
   */
  initDatepicker() {
    let monthToShow = new Date().getMonth();
    let yearToShow = new Date().getFullYear();

    if (this.$refs.fromInput && this.$refs.fromInput.value !== '') {
      const startDate = new Date(this.$refs.fromInput.value);
      if (!Number.isNaN(startDate.getTime())) {
        monthToShow = startDate.getMonth();
        yearToShow = startDate.getFullYear();
      }
    } else if (this.$options.goToDate) {
      const goToDate = new Date(this.$options.goToDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Si la date est dans le passé, on reste sur aujourd'hui
      if (!Number.isNaN(goToDate.getTime()) && goToDate >= today) {
        monthToShow = goToDate.getMonth();
        yearToShow = goToDate.getFullYear();
      }
    }

    this.calendar = new VanillaCalendar(this.$refs.container, {
      min: 'today',
      type: this.$options.single && !this.isMobile ? 'multiple' : 'default',
      months: 2,
      settings: {
        lang: 'define',
        visibility: {
          theme: 'light',
        },
        selection: {
          day: 'multiple-ranged',
        },
        range: {
          disablePast: true,
          disabled: [`${this.$options.startOff}:${this.$options.endOff}`],
        },
        selected: {
          dates: [this.$options.selectedDates],
          month: monthToShow,
          year: yearToShow,
        },
      },
      locale: {
        months: [
          'Janvier',
          'Février',
          'Mars',
          'Avril',
          'Mai',
          'Juin',
          'Juillet',
          'Août',
          'Septembre',
          'Octobre',
          'Novembre',
          'Décembre',
        ],
        weekday: ['D', 'L', 'M', 'M', 'J', 'V', 'S'],
      },
      actions: {
        clickDay: (e, self) => {
          const dates = self.selectedDates;

          if (dates.length > 1) {
            this.close();
            this.populateInputs(dates[0], dates.at(-1));

            nextTick(() => this.$emit('submit'));
          }

          this.populatePlaceholder(dates[0], dates.at(-1));
        },
      },
    });
  }

  /**
   * Check if device is mobile
   * @returns {boolean}
   */
  get isMobile() {
    return this.breakpoints.includes(this.$services.get('resized').breakpoint);
  }

  /**
   *
   */
  open() {
    if (!this.calendar.isInit) {
      this.$parent.open();
      this.calendar.init();
    }
    super.open();
  }

  /**
   * Fill inputs
   * @param  {string} from from date.
   * @param  {string} to   to date.
   * @returns {void}
   */
  populateInputs(from, to) {
    this.$refs.fromInput.value = from;
    this.$refs.toInput.value = to;
  }

  /**
   * Fill placeholder
   * @param  {string} from from date.
   * @param  {string} to   to date.
   * @returns {void}
   */
  populatePlaceholder(from, to) {
    if (from !== '') {
      this.$refs.placeholderStart.innerHTML = `du ${this.formattedDate(from)}`;
    }

    if (from !== to) {
      this.$refs.placeholderEnd.innerHTML = `au ${this.formattedDate(to)}`;
    }
  }

  /**
   * Format date
   * @param  {string} date date to format
   * @returns {string}
   */
  formattedDate(date) {
    const dateObj = new Date(date);

    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }).format(dateObj);
  }
}
